/**
 * LatencyBadge — the round-trip time to whatever is serving this video, drawn over the frame.
 *
 * One component for every live surface in both products (the cloud's Live Wall and Cameras page,
 * the local Cameras page), so "142ms" means and looks the same wherever it appears. The colour
 * thresholds are the ones the monitoring pages use, so "slow" reads the same too.
 *
 * `pointer-events-none`, always: it sits over a frame whose clicks belong to the player beneath,
 * and an overlay that eats them is worse than no overlay. Position is the caller's call — each
 * surface keeps its controls in different corners, and the badge must not cover them.
 */
import type { PeerLatency } from '../../hooks/usePeerLatency';

function rttInk(rtt: number): string {
  if (rtt <= 50) return 'text-green-300 font-bold';
  if (rtt <= 100) return 'text-yellow-200 font-bold';
  if (rtt <= 200) return 'text-orange-300 font-bold';
  return 'text-red-300 font-bold';
}

function rttFrame(rtt: number): string {
  if (rtt <= 50) return 'bg-black/60 border-green-400/60';
  if (rtt <= 100) return 'bg-black/60 border-yellow-400/60';
  if (rtt <= 200) return 'bg-black/60 border-orange-400/60';
  return 'bg-black/60 border-red-400/60';
}

export interface LatencyBadgeProps {
  latency: PeerLatency;
  /** Position (and any other) classes. Defaults to the top-right corner. */
  className?: string;
}

export default function LatencyBadge({ latency, className = 'top-1.5 right-1.5' }: LatencyBadgeProps) {
  return (
    <div
      className={`pointer-events-none absolute z-20 rounded border px-1.5 py-0.5 font-mono text-[10px] ${className} ${rttFrame(latency.rtt)}`}
      title={`Round-trip ${latency.rtt}ms · jitter ${latency.jitter}ms · ${latency.packetsLost} packets lost`}
    >
      <span className={rttInk(latency.rtt)}>{latency.rtt}ms</span>
      {latency.packetsLost > 0 && <span className="ml-1 font-bold text-red-300">↓{latency.packetsLost}</span>}
      {latency.jitter > 0 && <span className="ml-1 font-bold text-blue-300">±{latency.jitter}ms</span>}
    </div>
  );
}
